import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, CssBaseline, Box } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Provider } from 'react-redux'
import { store } from './store'
import theme from './theme'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import { SnackbarProvider } from './components/SnackbarProvider'
import LoginPage from './pages/LoginPage'
import HomePage from './pages/HomePage'
import ProfilePage from './pages/ProfilePage'
import PurchaseContractPage from './pages/PurchaseContractPage'
import QualityControlPage from './pages/QualityControlPage'
import SaleContractPage from './pages/SaleContractPage'
import SaleControlPage from './pages/SaleControlPage'
import ProcurementPage from './pages/ProcurementPage'
import FuturesControlPage from './pages/FuturesControlPage'
import FactoryPage from './pages/factory/FactoryPage'
import MatchingContractOverviewPage from './pages/matching/MatchingContractOverviewPage'
import PlaceholderPage from './pages/PlaceholderPage'
import ComingSoonPage from './pages/ComingSoonPage'
import ReportDashboardPage from './pages/ReportDashboardPage'
import MasterDataPage from './pages/MasterDataPage'
import SettingsPage from './pages/SettingsPage'

// Purchase module pages (synced)
import LocalPListPage from './pages/purchase/LocalPListPage'
import LocalPDetailPage from './pages/purchase/LocalPDetailPage'
import LocalPPlansPage from './pages/purchase/LocalPPlansPage'
import ContractsAllListPage from './pages/purchase/ContractsAllListPage'
import NPEContractListPage from './pages/purchase/NPEContractListPage'
import NPEContractDetailPage from './pages/purchase/NPEContractDetailPage'
import AnnexListPage from './pages/purchase/AnnexListPage'
import PTBFFixationListPage from './pages/purchase/PTBFFixationListPage'
import PTBFRollingListPage from './pages/purchase/PTBFRollingListPage'
import NPEFixationListPage from './pages/purchase/NPEFixationListPage'
import NPEAllocationListPage from './pages/purchase/NPEAllocationListPage'
import NPEAllocationWizard from './pages/purchase/NPEAllocationWizard'
import CSFixationListPage from './pages/purchase/CSFixationListPage'
import ForwardListPage from './pages/purchase/ForwardListPage'

// Vendors + Refs
import VendorsListPage from './pages/vendors/VendorsListPage'
import VendorDetailPage from './pages/vendors/VendorDetailPage'
import RefsHubPage from './pages/refs/RefsHubPage'
import BanksPage from './pages/refs/BanksPage'
import CalendarsPage from './pages/refs/CalendarsPage'
import CertificationsPage from './pages/refs/CertificationsPage'
import DeliveryPlacesPage from './pages/refs/DeliveryPlacesPage'
import DeliveryTermsPage from './pages/refs/DeliveryTermsPage'
import LicensesAdminPage from './pages/refs/LicensesAdminPage'
import PackagesPage from './pages/refs/PackagesPage'
import QualitiesPage from './pages/refs/QualitiesPage'
import AdjustLotsPage from './pages/refs/AdjustLotsPage'
import UpdatesPage from './pages/refs/UpdatesPage'

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
        <SnackbarProvider>
        <Box sx={{ height: '100%', width: '100%', margin: 0, padding: 0 }}>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/home" replace />} />
                <Route path="home" element={<HomePage />} />
                <Route path="profile" element={<ProfilePage />} />

                {/* Purchase Contract (legacy single-page) */}
                <Route path="purchase-contract" element={<PurchaseContractPage />} />
                <Route path="purchase/contract" element={<PurchaseContractPage />} />
                <Route path="purchase/contract/*" element={<PurchaseContractPage />} />

                {/* Purchase module — synced */}
                <Route path="purchase/local-p" element={<LocalPListPage />} />
                <Route path="purchase/local-p/:contractNo" element={<LocalPDetailPage />} />
                <Route path="purchase/contracts" element={<ContractsAllListPage />} />
                <Route path="purchase/npe-contracts" element={<NPEContractListPage />} />
                <Route path="purchase/npe-contracts/:contractNo" element={<NPEContractDetailPage />} />
                <Route path="purchase/annexes" element={<AnnexListPage />} />
                <Route path="purchase/ptbf-fixations" element={<PTBFFixationListPage />} />
                <Route path="purchase/ptbf-rollings" element={<PTBFRollingListPage />} />
                <Route path="purchase/npe-fixations" element={<NPEFixationListPage />} />
                <Route path="purchase/npe-allocations" element={<NPEAllocationListPage />} />
                <Route path="purchase/npe-allocations/new" element={<NPEAllocationWizard />} />
                <Route path="purchase/cs-fixations" element={<CSFixationListPage />} />
                <Route path="purchase/forward" element={<ForwardListPage />} />
                <Route path="purchase/plans" element={<LocalPPlansPage />} />

                {/* Vendors */}
                <Route path="vendors" element={<VendorsListPage />} />
                <Route path="vendors/:code" element={<VendorDetailPage />} />

                {/* References */}
                <Route path="purchase/refs" element={<RefsHubPage />} />
                <Route path="purchase/refs/banks" element={<BanksPage />} />
                <Route path="purchase/refs/calendars" element={<CalendarsPage />} />
                <Route path="purchase/refs/certifications" element={<CertificationsPage />} />
                <Route path="purchase/refs/delivery-places" element={<DeliveryPlacesPage />} />
                <Route path="purchase/refs/delivery-terms" element={<DeliveryTermsPage />} />
                <Route path="purchase/refs/licenses" element={<LicensesAdminPage />} />
                <Route path="purchase/refs/packages" element={<PackagesPage />} />
                <Route path="purchase/refs/qualities" element={<QualitiesPage />} />
                <Route path="purchase/refs/adjust-lots" element={<AdjustLotsPage />} />
                <Route path="purchase/refs/updates" element={<UpdatesPage />} />

                {/* Report / Dashboard — Power BI embed */}
                <Route path="dashboard/*" element={<ReportDashboardPage />} />

                {/* Sales */}
                <Route path="sales/contract" element={<SaleContractPage />}>
                  <Route index element={<Navigate to="/sales/contract/list" replace />} />
                  <Route path="list" element={null} />
                  <Route path="consignment" element={null} />
                  <Route path="amendment" element={null} />
                  <Route path="ptbf-fixation" element={null} />
                  <Route path="references" element={null} />
                </Route>
                <Route path="sales/control" element={<SaleControlPage />} />
                <Route path="sales/control/*" element={<SaleControlPage />} />

                {/* Procurement */}
                <Route path="procurement" element={<ProcurementPage />} />
                <Route path="procurement/*" element={<ProcurementPage />} />

                {/* Futures Control */}
                <Route path="futures" element={<FuturesControlPage />} />
                <Route path="futures/*" element={<FuturesControlPage />} />

                {/* Factory */}
                {/* Legacy redirect: `/factory/bao-loc/*` → `/factory/baoloc/*`. */}
                <Route path="factory/bao-loc" element={<Navigate to="/factory/baoloc" replace />} />
                <Route path="factory/bao-loc/*" element={<Navigate to="/factory/baoloc" replace />} />
                {/* Factory Summary parked behind ComingSoonPage until rebuild lands.
                    Must be declared BEFORE `factory/:factoryCode` so React Router matches
                    the literal path first. Restore: delete this route and let `/factory/summary`
                    fall through to the dedicated summary page (currently no such page exists;
                    will need to be wired up during rebuild). */}
                <Route path="factory/summary" element={<ComingSoonPage />} />
                <Route path="factory/:factoryCode" element={<FactoryPage />} />
                <Route path="factory/:factoryCode/*" element={<FactoryPage />} />
                <Route path="factory/*" element={<PlaceholderPage />} />

                {/* Matching P - S — mirror Access form `frmMatchingPSMain` */}
                <Route path="matching" element={<MatchingContractOverviewPage />} />
                <Route path="matching/:tab" element={<MatchingContractOverviewPage />} />

                {/* Quality */}
                <Route path="quality" element={<QualityControlPage />} />
                <Route path="quality/*" element={<QualityControlPage />} />

                {/* Virtual Warehouse — all tabs parked behind ComingSoonPage until rebuild lands.
                    Title is auto-derived from the nav breadcrumb (Received / Quality Discount /
                    Delivery Note Print / Receive Note Print). Restore by swapping the element
                    back to a dedicated page that mounts the real views. */}
                <Route path="virtual-warehouse/*" element={<ComingSoonPage />} />


                {/* Master Data — single tabbed page (frmMdMain). MasterDataPage
                    drives top tab + inner tab + "Go to Page" Select from URL. */}
                <Route path="master-data/*" element={<MasterDataPage />} />

                {/* Settings */}
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/*" element={<SettingsPage />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/home" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </Box>
        </SnackbarProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  )
}

export default App
